import { useCallback, useEffect, useMemo, useState } from 'react';
import { pmGet, pmPost, type PmProgramPlan, type PmPhaseRow, type PmProject } from '../api';
import './ResourcePlan.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Resource Plan — the per-opportunity staffing baseline, in FTE.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/resources.ejs`.
 * The screen's own description, from its help panel:
 *
 *   Set the staffing baseline per opportunity — a program-wide Shared bucket (PMO, shared
 *   roles) plus each opportunity by phase, in FTE.
 *
 * ITS OWN COMPONENT, deliberately not Budget Plan parameterised by unit. The two source
 * views are separate files and differ in more than the unit: a different header icon
 * (`groups`, not `account_balance_wallet`), TWO stat cards rather than four (there is no
 * approved staffing model to vary against), a 0.1 step rather than 1000, and a narrower
 * override input. CLAUDE.md §1.2.1 — a port is not a refactor.
 */

const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#90caf9',
  Design_Architecture: '#ce93d8',
  Development: '#a5d6a7',
  Testing_Validation: '#ffcc80',
  GoLive_Optimization: '#ef9a9a',
  Agile_Delivery: '#80cbc4',
};

/** One decimal place, then the unit — the source's `fmt`. */
const fmt = (n: unknown) =>
  `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('en-US')} FTE`;

const effOf = (r: PmPhaseRow) =>
  r.override !== null && (r.override as unknown) !== '' ? Number(r.override) : Number(r.proposed) || 0;

const isOverridden = (r: PmPhaseRow) => r.override !== null && (r.override as unknown) !== '';

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/resources.ejs`,
 * its `resHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const RESOURCES_HELP: HelpContent = {
    title: 'Resource Plan',
    icon: '👥',
    purpose: "Set the <strong>staffing baseline per opportunity</strong> — a project&#8209;wide <strong>Shared</strong> bucket (PMO, shared roles) plus <strong>each opportunity</strong> by phase, in <strong>FTE</strong>. Each opportunity's per&#8209;phase FTE is <strong>proposed automatically</strong> from its resource allocation, and you can <strong>override</strong> any row.",
    doHere: [
      "An opportunity whose methodology is <strong>None</strong> has <strong>no phases</strong> &mdash; it appears here as a single <strong>Delivery</strong> row covering its whole window, and is fully resourceable just like any other initiative.",
      "Select a <strong>Project</strong>; its Shared bucket and opportunities load below.",
      "In <strong>Shared / Project</strong>, enter project&#8209;level FTE per phase.",
      "Open each <strong>opportunity</strong> and accept the proposed per&#8209;phase FTE or type a manual <strong>override</strong>.",
      "Click <strong>Save</strong> to lock the staffing baseline the Resource Tracker measures actual effort against."
    ],
    fits: 'Budget Plan &rarr; <strong>Resource Plan</strong> &rarr; Execution Control Center &rarr; Resource Tracker',
    firstTime: "Pick a project, review each opportunity's auto-proposed per-phase FTE, adjust any row, then Save.",
    sections: [
      { label: 'How the plan is structured', type: 'defs', items: [
        { term: 'Shared', color: '#6a1b9a', def: "Project&#8209;level FTE for roles not tied to one opportunity (PMO, shared architects). Entered manually per phase." },
        { term: 'Opportunity', color: '#1976d2', def: "Each opportunity carries its <strong>own</strong> per&#8209;phase FTE — proposed from its resource allocation, override per row. The project total rolls up Shared + every opportunity." },
        { term: 'Phase', color: '#42a5f5', def: "Every opportunity is staffed by the five delivery phases (Assessment → Go&#8209;Live)." }
      ] },
      { label: 'Proposed vs. override', type: 'html', html: "The <strong>proposed</strong> FTE is the opportunity's <strong>allocated FTE</strong> from the resource model. Type an <strong>override</strong> to replace a row; leave blank to keep the proposal. The <strong>Effective</strong> FTE carries forward to the Resource Tracker." },
      { label: "What's new", type: 'html', html: "The Resource Plan is a <strong>tree grid</strong>: a Shared/Project bucket plus each opportunity as a <strong>group row</strong> you expand to reveal its five <strong>phase rows</strong> (each marked by its phase-colored box). Type an override on any phase row; the group row shows that scope's Effective FTE." }
    ],
    links: [ { label: 'Next: Resource Tracker →', href: '/project_management/program-management/budget-resources/resource-tracker' } ]
  };

export function ResourcePlan() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [currentProject, setCurrentProject] = useState('');
  const [prog, setProg] = useState<PmProgramPlan | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Save');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pmGet<{ projects: PmProject[] }>('/br/projects')
      .then((d) => setProjects(d.projects ?? []))
      .catch((e) => setError((e as Error).message));
  }, []);

  const onSelect = useCallback(async (id: string) => {
    setCurrentProject(id);
    setError(null);
    if (!id) {
      setProg(null);
      return;
    }
    try {
      const d = await pmGet<PmProgramPlan>(
        `/resources/program-data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setExpanded(new Set());
      setProg(d);
      setDirty(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  const scopePhases = useCallback(
    (id: string): PmPhaseRow[] => {
      if (!prog) return [];
      if (id === 'shared') return prog.shared.phases;
      return prog.opportunities.find((x) => `opp:${x.opportunity_id}` === id)?.phases ?? [];
    },
    [prog],
  );

  const scopeTotal = useCallback(
    (id: string) => scopePhases(id).reduce((s, r) => s + effOf(r), 0),
    [scopePhases],
  );

  const stats = useMemo(() => {
    if (!prog) return null;
    let planned = scopeTotal('shared');
    let proposed = 0;
    prog.opportunities.forEach((op) => {
      planned += scopeTotal(`opp:${op.opportunity_id}`);
      op.phases.forEach((p) => {
        proposed += Number(p.proposed) || 0;
      });
    });
    return { planned, proposed };
  }, [prog, scopeTotal]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = (on: boolean) => {
    if (!prog) return;
    setExpanded(
      on
        ? new Set(['shared', ...prog.opportunities.map((o) => `opp:${o.opportunity_id}`)])
        : new Set(),
    );
  };

  const setOv = (scopeId: string, phase: string, val: string) => {
    const row = scopePhases(scopeId).find((x) => x.phase === phase);
    if (!row) return;
    row.override = val === '' ? null : (val as unknown as number);
    setProg((p) => (p ? { ...p } : p));
    setDirty(true);
  };

  const save = async () => {
    if (!currentProject || !prog) return;
    setSaving(true);
    setSaveLabel('Saving…');
    const amount = (r: PmPhaseRow) =>
      (r.override as unknown) === '' || r.override == null ? null : Number(r.override);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/resources/program-save', {
        delivery_project_id: currentProject,
        shared: prog.shared.phases.map((r) => ({ phase: r.phase, amount: amount(r) })),
        opportunities: prog.opportunities.map((o) => ({
          opportunity_id: o.opportunity_id,
          overrides: o.phases.map((r) => ({ phase: r.phase, amount: amount(r) })),
        })),
      });
      if (d.success) {
        setDirty(false);
        setSaveLabel('Saved ✓');
        setTimeout(() => setSaveLabel('Save'), 1100);
        await onSelect(currentProject);
      } else {
        setError(d.error ?? 'failed');
        setSaveLabel('Save');
      }
    } catch (e) {
      setError((e as Error).message);
      setSaveLabel('Save');
    } finally {
      setSaving(false);
    }
  };

  const plannedTotal = prog
    ? prog.opportunities.reduce(
        (s, o) => s + scopeTotal(`opp:${o.opportunity_id}`),
        scopeTotal('shared'),
      )
    : 0;

  const renderScope = (
    id: string,
    title: string,
    op: PmProgramPlan['opportunities'][number] | null,
  ) => {
    const isShared = !op;
    const open = expanded.has(id);
    const accent = isShared ? '#6a1b9a' : '#1976d2';
    const phases = scopePhases(id);
    const prop = phases.reduce((s, r) => s + (Number(r.proposed) || 0), 0);
    const tot = phases.reduce((s, r) => s + effOf(r), 0);

    const rows = [
      <tr className="grp" key={id} onClick={() => toggle(id)}>
        <td className="ind">
          <i
            className="material-icons pm-rp-caret"
            style={{ transform: `rotate(${open ? 90 : 0}deg)` }}
          >
            chevron_right
          </i>
        </td>
        <td>
          <div className="pm-rp-scopecell">
            <span className="pm-rp-dot" style={{ background: accent }} />
            {title} {op ? <span className="pm-rp-oppname">{op.name || ''}</span> : null}
          </div>
        </td>
        <td className="num">{isShared ? '—' : fmt(prop)}</td>
        <td className="num" />
        <td className="num pm-rp-eff">{fmt(tot)}</td>
      </tr>,
    ];

    phases.forEach((r) => {
      const overridden = isOverridden(r);
      rows.push(
        <tr className="phrow" key={`${id}-${r.phase}`} style={{ display: open ? '' : 'none' }}>
          <td className="ind" />
          <td className="ph-cell">
            <span
              className="pm-rp-phbox"
              style={{ background: PHASE_COLORS[r.phase] || '#b0bec5' }}
              title={r.label}
            />
            <span className="pm-rp-phlabel">{r.label}</span>
          </td>
          {isShared ? (
            <td className="num" style={{ color: '#b0bec5' }}>
              —
            </td>
          ) : (
            <td className="num">
              {fmt(r.proposed)}
              <span className="pm-rp-srctag">model</span>
            </td>
          )}
          <td className="num">
            <input
              type="number"
              className="pm-rp-ovinput"
              min={0}
              step={0.1}
              placeholder={isShared ? '0' : '(use proposed)'}
              value={overridden ? String(r.override) : ''}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setOv(id, r.phase, e.target.value)}
            />
          </td>
          <td className="num pm-rp-eff">
            {fmt(effOf(r))}
            <span
              className="pm-rp-srctag"
              style={overridden ? { color: '#1565c0', background: '#e8f1fb' } : undefined}
            >
              {id === 'shared' ? 'shared' : overridden ? 'override' : 'proposed'}
            </span>
          </td>
        </tr>,
      );
    });
    return rows;
  };

  return (
    <div
      className="page"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      <HelpPanel help={withFinalizedRule(RESOURCES_HELP)} />
      <div className="pm-rp-head">
        <div className="pm-rp-titleblock">
          <i className="material-icons" style={{ fontSize: '2.1rem', color: '#1976d2' }}>
            groups
          </i>
          <div>
            <h1 className="page-header__title">Resource Plan</h1>
            <p>
              Per-opportunity FTE baseline — a Shared bucket plus each opportunity by phase,
              proposed from the resource model.
            </p>
          </div>
        </div>
        <div className="pm-rp-toolbar">
          <div className="pm-rp-fg">
            <SelectField
              label="Project"
              width="lg"
              placeholder={PROMPT.project}
              value={currentProject}
              onChange={(v) => void onSelect(v)}
              options={projects.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
          </div>
          {prog ? (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="pm-rp-linkbtn" onClick={() => expandAll(true)}>
                Expand all
              </button>
              <button className="pm-rp-linkbtn" onClick={() => expandAll(false)}>
                Collapse all
              </button>
              <span style={{ color: '#cfd8dc' }}>|</span>
              <span className="pm-rp-summary">Planned {fmt(plannedTotal)}</span>
              <button className="pm-rp-save" disabled={!dirty || saving} onClick={() => void save()}>
                {saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="pm-rp-body">
        {error ? (
          <div className="pm-rp-empty" style={{ color: '#c62828' }}>
            {error}
          </div>
        ) : null}
        {!prog ? (
          <div className="pm-rp-empty">
            Select a program to view its staffing by opportunity and phase.
          </div>
        ) : (
          <>
            {stats ? (
              <div className="pm-rp-statrow">
                <div className="pm-rp-stat">
                  <i className="material-icons ic" style={{ color: '#43a047' }}>
                    groups
                  </i>
                  <div>
                    <div className="lbl">Planned Staffing</div>
                    <div className="val" style={{ color: '#1565c0' }}>
                      {fmt(stats.planned)}
                    </div>
                    <div className="sub">Shared + all opportunities</div>
                  </div>
                </div>
                <div className="pm-rp-stat">
                  <i className="material-icons ic" style={{ color: '#8e24aa' }}>
                    insights
                  </i>
                  <div>
                    <div className="lbl">Resource-model Proposal</div>
                    <div className="val">{fmt(stats.proposed)}</div>
                    <div className="sub">allocated FTE from the model</div>
                  </div>
                </div>
              </div>
            ) : null}

            <table className="pm-rp-grid">
              <colgroup>
                <col style={{ width: 44 }} />
                <col />
                <col style={{ width: 160 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 160 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="ind" />
                  <th>Scope / Phase</th>
                  <th className="num">Proposed</th>
                  <th className="num">Override</th>
                  <th className="num">Effective</th>
                </tr>
              </thead>
              <tbody>
                {renderScope('shared', '🌐 Shared / Program', null)}
                {prog.opportunities.map((op) =>
                  renderScope(`opp:${op.opportunity_id}`, `📌 ${op.opportunity_id}`, op),
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
